import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Instagram, Loader2, Sparkles } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { ASSETS, TEXTURES, MESSAGES_LINKS } from "@/lib/assets";

export default function Roster() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email, artist: "MESSAGES" });
      toast.success("You're on the list — MESSAGES release alerts incoming.");
      setEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="roster-page" className="pt-32 md:pt-40 bg-charcoal">
      <div className="max-w-[1400px] mx-auto px-4 md:px-12 lg:px-16 pb-24 md:pb-32">
        <Reveal>
          <p className="font-label uppercase tracking-[0.35em] text-crimson-bright text-xs mb-4">The Catalog</p>
          <h1 className="font-display font-black text-5xl md:text-7xl text-softwhite tracking-tight">LABEL ROSTER</h1>
          <p className="mt-5 text-slate text-lg font-body">The sonic catalog of Sorrow &amp; Serenity Music.</p>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-2 gap-8">
          {/* CARD 1 — MESSAGES */}
          <Reveal>
            <article className="border border-slate/20 bg-surface h-full flex flex-col" data-testid="roster-card-messages">
              <Link
                to="/roster/messages"
                data-testid="roster-messages-link"
                className="group block"
              >
                <div className="relative h-72 md:h-80 flex items-center justify-center overflow-hidden border-b border-slate/20 bg-charcoal transition-transform duration-500 group-hover:scale-[1.02]">
                  <img
                    src={ASSETS.messagesLogoV2}
                    alt="MESSAGES"
                    className="relative z-10 max-h-full w-full object-contain px-8 py-6"
                  />
                </div>
                <div className="p-8 md:p-10 pb-0">
                  <span className="inline-block px-3 py-1.5 bg-crimson/20 border border-crimson/50 font-label uppercase tracking-[0.2em] text-[11px] text-crimson-bright">
                    New Single — Fall 2026
                  </span>
                  <h2 className="mt-6 font-display text-4xl text-softwhite transition-colors duration-300 group-hover:text-crimson-bright">
                    MESSAGES
                  </h2>
                  <p className="mt-3 font-label uppercase tracking-[0.12em] text-[11px] text-slate">
                    Modern Metal / Gothic Alternative / Atmospheric
                  </p>
                  <p className="mt-5 text-slate leading-relaxed font-body">
                    Blending crushing modern metal grooves with soaring gothic atmosphere and dark melodic hooks. New
                    single dropping Fall 2026 via Sorrow &amp; Serenity Music.
                  </p>
                  <p className="mt-5 inline-flex items-center gap-2 font-label uppercase tracking-[0.2em] text-[11px] text-crimson-bright">
                    View Artist Page
                    <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
                  </p>
                </div>
              </Link>
              <div className="p-8 md:p-10 pt-6 flex flex-col flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <a
                    href={MESSAGES_LINKS.spotify}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="messages-spotify"
                    className="group inline-flex items-center gap-2.5 bg-[#1DB954] hover:bg-[#1ed760] text-black px-6 py-3 font-label uppercase tracking-[0.12em] text-sm transition-colors duration-300"
                  >
                    <i className="fa-brands fa-spotify text-base" aria-hidden="true" />
                    Listen on Spotify
                  </a>
                  <a
                    href={MESSAGES_LINKS.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="messages-instagram"
                    aria-label="Instagram"
                    className="inline-flex items-center justify-center h-11 w-11 border border-slate/40 text-slate hover:text-crimson-bright hover:border-crimson-bright transition-colors"
                  >
                    <Instagram size={20} />
                  </a>
                </div>

                {/* Newsletter */}
                <form onSubmit={subscribe} className="mt-8 pt-8 border-t border-slate/15" data-testid="newsletter-form">
                  <label className="font-label uppercase tracking-[0.2em] text-[11px] text-slate">Release Alerts</label>
                  <div className="mt-3 flex flex-col sm:flex-row gap-3">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter email for release alerts"
                      data-testid="newsletter-email-input"
                      className="flex-1 bg-charcoal border border-slate/30 focus:border-crimson px-4 py-3 text-softwhite placeholder:text-slate/60 outline-none transition-colors font-body"
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="newsletter-submit-btn"
                      className="inline-flex items-center justify-center gap-2 bg-crimson hover:bg-crimson-bright disabled:opacity-60 text-softwhite px-6 py-3 font-label uppercase tracking-[0.15em] text-sm transition-colors duration-300"
                    >
                      {loading ? <Loader2 size={16} className="animate-spin" /> : "Sign Up"}
                    </button>
                  </div>
                </form>
              </div>
            </article>
          </Reveal>

          {/* CARD 2 — Future Roster */}
          <Reveal delay={0.15}>
            <article className="border border-dashed border-slate/30 bg-surface/40 h-full flex flex-col" data-testid="roster-card-future">
              <div className="relative h-64 flex items-center justify-center overflow-hidden border-b border-slate/20">
                <img src={TEXTURES.gothic} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
                <div className="absolute inset-0 bg-charcoal/60" />
                <img src={ASSETS.labelLogo} alt="Sorrow & Serenity Seal" className="relative z-10 h-40 w-40 object-contain opacity-80" />
              </div>
              <div className="p-8 md:p-10 flex flex-col flex-1 justify-center">
                <span className="inline-flex items-center gap-2 self-start px-3 py-1.5 border border-slate/40 font-label uppercase tracking-[0.2em] text-[11px] text-slate">
                  <Sparkles size={13} /> Expanding
                </span>
                <h2 className="mt-6 font-display text-3xl text-softwhite/80">Future Roster Addition</h2>
                <p className="mt-5 text-slate leading-relaxed font-body">
                  Sorrow &amp; Serenity Music is actively seeking high-caliber atmospheric rock and modern metal talent.
                </p>
              </div>
            </article>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
